/**
 * Engineering Upload API
 * Wraps the Engineering Service /upload endpoints.
 * All requests go directly to ENG_SERVICE_URL, bypassing the general backend.
 */

import { ENG_SERVICE_URL, ENG_API_KEY } from './apiConfig';

/** Build the common headers for every eng-service request */
const engHeaders = (userEmail, extra = {}) => ({
  'X-Eng-Api-Key': ENG_API_KEY,
  'X-Uploaded-By': userEmail || 'anonymous',
  ...extra,
});

/** Throw early if ENG_SERVICE_URL was not configured */
function assertEngUrl() {
  if (!ENG_SERVICE_URL) {
    throw new Error(
      'Engineering Service URL is not configured. ' +
      'Set REACT_APP_ENG_SERVICE_URL in the Azure Static Web App environment variables.'
    );
  }
}

/**
 * Upload one file to the Engineering Service.
 * Returns { job_id, doc_id, filename, status: "queued" }
 *
 * NOTE: The eng API accepts ONE file per request.
 *
 * @param {File} file
 * @param {string} userEmail
 * @returns {Promise<{job_id: string, doc_id: string, filename: string, status: string}>}
 */
export async function uploadEngFile(file, userEmail) {
  assertEngUrl();
  const form = new FormData();
  form.append('file', file);

  const res = await fetch(`${ENG_SERVICE_URL}/upload`, {
    method: 'POST',
    headers: engHeaders(userEmail),
    // Content-Type must NOT be set manually — browser sets multipart boundary
    body: form,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Upload failed (${res.status})`);
  }
  return res.json();
}

/**
 * Poll /upload/status/{job_id} every 2 s until status is "ready" or "failed".
 * Calls onProgress(status) on every tick.
 *
 * @param {string} jobId
 * @param {(status: string) => void} [onProgress]
 * @returns {Promise<object>} Final JobStatus object
 */
export async function pollEngJobStatus(jobId, onProgress) {
  assertEngUrl();
  const MAX_ATTEMPTS = 150; // 5 minutes max (150 * 2 s)
  let attempts = 0;

  while (attempts < MAX_ATTEMPTS) {
    const res = await fetch(`${ENG_SERVICE_URL}/upload/status/${jobId}`, {
      headers: engHeaders(''),
    });

    if (!res.ok) {
      if (res.status === 404) {
        throw new Error('Job not found — the service may have restarted. Check the document list.');
      }
      throw new Error(`Status check failed (${res.status})`);
    }

    const job = await res.json();
    onProgress?.(job.status);

    if (job.status === 'ready' || job.status === 'failed') {
      return job;
    }

    attempts++;
    await new Promise((r) => setTimeout(r, 2000));
  }

  throw new Error('Processing timed out. Check the Document Manager to see if the document was indexed.');
}

/**
 * List all successfully indexed engineering documents for userEmail.
 * Returns the normalized UploadedDoc array with `id` aliased from `doc_id`.
 *
 * @param {string} userEmail
 * @returns {Promise<Array<object>>}
 */
export async function listEngDocs(userEmail) {
  assertEngUrl();
  const res = await fetch(`${ENG_SERVICE_URL}/upload/list`, {
    headers: engHeaders(userEmail),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to list documents (${res.status})`);
  }

  const data = await res.json();
  // Normalize: add `id` alias for compatibility with DocumentManager expectations
  return (data.documents || []).map((doc) => ({
    ...doc,
    id: doc.doc_id,
    uploaded_at: doc.upload_timestamp,
  }));
}

/**
 * Delete an engineering document by doc_id.
 * Returns { deleted, doc_id, chunks_removed, filename }
 *
 * @param {string} docId
 * @param {string} userEmail
 * @returns {Promise<object>}
 */
export async function deleteEngDoc(docId, userEmail) {
  assertEngUrl();
  const res = await fetch(`${ENG_SERVICE_URL}/upload/${docId}`, {
    method: 'DELETE',
    headers: engHeaders(userEmail),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to delete document (${res.status})`);
  }
  return res.json();
}
